import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  ChevronDown,
  ChevronUp,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  Star,
  AlertTriangle,
  BookOpen,
  Calendar,
  Heart,
  Users,
  GraduationCap,
  ClipboardCheck,
  FileText,
  MessageSquare,
  Plus,
  Trash2,
  Info
} from 'lucide-react';

// Constants for dropdown options
const YES_NO_UNKNOWN = [
  { value: '', label: 'Select...' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unknown', label: 'Unknown' }
];

const DELIVERED_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'partially', label: 'Partially' },
  { value: 'unknown', label: 'Unknown' }
];

const DECISION_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'continue_current', label: 'Continue current intervention' },
  { value: 'adjust_intervention', label: 'Adjust intervention' },
  { value: 'increase_intensity', label: 'Increase intensity' },
  { value: 'decrease_intensity', label: 'Decrease intensity' },
  { value: 'move_to_monitoring', label: 'Move to monitoring' },
  { value: 'exit_progress', label: 'Exit intervention due to progress' },
  { value: 'consider_sped_referral', label: 'Consider special education referral' },
  { value: 'need_more_data', label: 'Need more data before decision' }
];

const SUPPORT_AREAS = [
  { value: '', label: 'Select area...' },
  { value: 'reading', label: 'Reading' },
  { value: 'writing', label: 'Writing' },
  { value: 'math', label: 'Math' },
  { value: 'behavior', label: 'Behavior / Executive Function' },
  { value: 'speech_language', label: 'Speech / Language' },
  { value: 'social_skills', label: 'Social Skills' },
  { value: 'attendance', label: 'Attendance / Engagement' },
  { value: 'other', label: 'Other' }
];

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    complete: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Complete' },
    partial: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock, label: 'Partial' },
    missing: { bg: 'bg-gray-100', text: 'text-gray-500', icon: AlertCircle, label: 'Missing' }
  };
  
  const { bg, text, icon: Icon, label } = config[status] || config.missing;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
};

// Collapsible section component
const ProfileSection = ({ 
  title, 
  icon: Icon, 
  status, 
  children, 
  defaultExpanded = false,
  testId
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  return (
    <div className="border border-[#E5E5E5] rounded-xl overflow-hidden bg-white" data-testid={testId}>
      <button
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#2C4C3B]/10">
            <Icon className="h-4 w-4 text-[#2C4C3B]" />
          </div>
          <span className="font-medium text-[#1C1C1C]">{title}</span>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={status} />
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-[#E5E5E5] pt-4">
          {children}
        </div>
      )}
    </div>
  );
};

// Form field components
const TextField = ({ label, value, onChange, placeholder, multiline = false, rows = 3 }) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-[#595959]">{label}</label>
    {multiline ? (
      <textarea
        className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/20 focus:border-[#2C4C3B]"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
      />
    ) : (
      <input
        type="text"
        className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/20 focus:border-[#2C4C3B]"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    )}
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-[#595959]">{label}</label>
    <select
      className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/20 focus:border-[#2C4C3B] bg-white"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

const DateField = ({ label, value, onChange }) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-[#595959]">{label}</label>
    <input
      type="date"
      className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/20 focus:border-[#2C4C3B]"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

export default function StudentContextProfile({ studentId, canEdit = false }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadProfile();
  }, [studentId]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await apiService.getStudentContextProfile(studentId);
      setProfile(res.data);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to load context profile:', error);
      toast.error('Failed to load context profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiService.updateStudentContextProfile(studentId, profile);
      setProfile(res.data);
      setHasChanges(false);
      toast.success('Context profile saved');
    } catch (error) {
      console.error('Failed to save context profile:', error);
      toast.error('Failed to save context profile');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (section, field, value) => {
    setProfile(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  // Helper to add entry to list sections
  const addEntry = (section, defaultEntry) => {
    setProfile(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        entries: [...(prev[section]?.entries || []), defaultEntry]
      }
    }));
    setHasChanges(true);
  };

  const updateEntry = (section, index, field, value) => {
    setProfile(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        entries: prev[section].entries.map((entry, i) => 
          i === index ? { ...entry, [field]: value } : entry
        )
      }
    }));
    setHasChanges(true);
  };

  const removeEntry = (section, index) => {
    setProfile(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        entries: prev[section].entries.filter((_, i) => i !== index)
      }
    }));
    setHasChanges(true);
  };

  // Calculate overall completeness
  const getOverallCompleteness = () => {
    if (!profile?.completion_status) return { complete: 0, partial: 0, missing: 10 };
    const status = profile.completion_status;
    const counts = { complete: 0, partial: 0, missing: 0 };
    Object.values(status).forEach(s => {
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B]" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12 text-gray-500">
        Failed to load context profile
      </div>
    );
  }

  const completeness = getOverallCompleteness();
  const status = profile.completion_status || {};

  return (
    <div className="space-y-6" data-testid="student-context-profile">
      {/* Context Completeness Panel */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-5" data-testid="context-completeness-panel">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-[#1C1C1C] flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-[#2C4C3B]" />
              Context Completeness
            </h3>
            <p className="text-sm text-[#595959] mt-1">
              This profile helps the team understand the full student context before making intervention or referral decisions.
            </p>
          </div>
          {canEdit && (
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white rounded-xl"
              data-testid="save-context-profile-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Profile
            </Button>
          )}
        </div>

        {/* Completion Summary */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs font-bold">
              {completeness.complete}
            </span>
            <span className="text-sm text-[#595959]">Complete</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
              {completeness.partial}
            </span>
            <span className="text-sm text-[#595959]">Partial</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs font-bold">
              {completeness.missing}
            </span>
            <span className="text-sm text-[#595959]">Missing</span>
          </div>
        </div>

        {/* Status Message */}
        {completeness.missing >= 7 ? (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
              <p className="text-sm text-amber-700">
                Several context areas still need information before this student would be ready for a strong review decision.
              </p>
            </div>
          </div>
        ) : completeness.complete >= 7 ? (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
              <p className="text-sm text-green-700">
                This student has a strong context profile to support MTSS review decisions.
              </p>
            </div>
          </div>
        ) : (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-600 mt-0.5" />
              <p className="text-sm text-blue-700">
                Continue adding information to build a complete picture for intervention decisions.
              </p>
            </div>
          </div>
        )}

        {/* Last Updated */}
        {profile.updated_at && (
          <p className="text-xs text-[#A3A3A3] mt-3">
            Last updated: {new Date(profile.updated_at).toLocaleDateString()} 
            {profile.updated_by_name && ` by ${profile.updated_by_name}`}
          </p>
        )}
      </div>

      {/* Future Handoff Note */}
      <div className="bg-[#2C4C3B]/5 border border-[#2C4C3B]/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <FileText className="h-5 w-5 text-[#2C4C3B] mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[#2C4C3B]">Future FrameEd Handoff</p>
            <p className="text-sm text-[#595959] mt-1">
              This information will eventually support Review of Existing Data generation if the team begins a special education evaluation.
            </p>
          </div>
        </div>
      </div>

      {/* Profile Sections */}
      <div className="space-y-3">
        {/* 1. Student Strengths */}
        <ProfileSection 
          title="Student Strengths" 
          icon={Star} 
          status={status.strengths}
          testId="section-strengths"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="Academic Strengths"
              value={profile.strengths?.academic}
              onChange={(v) => updateSection('strengths', 'academic', v)}
              placeholder="What academic areas does the student excel in?"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Communication Strengths"
              value={profile.strengths?.communication}
              onChange={(v) => updateSection('strengths', 'communication', v)}
              placeholder="How does the student communicate effectively?"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Social / Behavioral Strengths"
              value={profile.strengths?.social_behavioral}
              onChange={(v) => updateSection('strengths', 'social_behavioral', v)}
              placeholder="Positive social or behavioral qualities"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Interests / Motivators"
              value={profile.strengths?.interests_motivators}
              onChange={(v) => updateSection('strengths', 'interests_motivators', v)}
              placeholder="What motivates or interests the student?"
              multiline
              disabled={!canEdit}
            />
            <div className="md:col-span-2">
              <TextField
                label="Helpful Supports or Conditions"
                value={profile.strengths?.helpful_supports}
                onChange={(v) => updateSection('strengths', 'helpful_supports', v)}
                placeholder="What conditions or supports help the student succeed?"
                multiline
                disabled={!canEdit}
              />
            </div>
          </div>
        </ProfileSection>

        {/* 2. Current Concerns */}
        <ProfileSection 
          title="Current Concerns" 
          icon={AlertTriangle} 
          status={status.current_concerns}
          testId="section-concerns"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="Primary Concern"
              value={profile.current_concerns?.primary_concern}
              onChange={(v) => updateSection('current_concerns', 'primary_concern', v)}
              placeholder="Main reason for intervention support"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Secondary Concerns"
              value={profile.current_concerns?.secondary_concerns}
              onChange={(v) => updateSection('current_concerns', 'secondary_concerns', v)}
              placeholder="Additional areas of concern"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="When Concerns Were First Noticed"
              value={profile.current_concerns?.when_first_noticed}
              onChange={(v) => updateSection('current_concerns', 'when_first_noticed', v)}
              placeholder="e.g., Beginning of 2nd grade, Fall 2025"
              disabled={!canEdit}
            />
            <TextField
              label="Settings Where Concerns Are Observed"
              value={profile.current_concerns?.settings_observed}
              onChange={(v) => updateSection('current_concerns', 'settings_observed', v)}
              placeholder="e.g., Classroom, small group, independent work"
              disabled={!canEdit}
            />
            <TextField
              label="How Concerns Affect Classroom Performance"
              value={profile.current_concerns?.classroom_impact}
              onChange={(v) => updateSection('current_concerns', 'classroom_impact', v)}
              placeholder="Impact on academic performance"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="How Concerns Affect Participation / Independence"
              value={profile.current_concerns?.participation_impact}
              onChange={(v) => updateSection('current_concerns', 'participation_impact', v)}
              placeholder="Impact on participation or independence"
              multiline
              disabled={!canEdit}
            />
          </div>
        </ProfileSection>

        {/* 3. Educational History */}
        <ProfileSection 
          title="Educational History" 
          icon={BookOpen} 
          status={status.educational_history}
          testId="section-history"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="Previous Schools (if known)"
              value={profile.educational_history?.previous_schools}
              onChange={(v) => updateSection('educational_history', 'previous_schools', v)}
              placeholder="Prior schools attended"
              disabled={!canEdit}
            />
            <TextField
              label="Grade Retention (if applicable)"
              value={profile.educational_history?.grade_retention}
              onChange={(v) => updateSection('educational_history', 'grade_retention', v)}
              placeholder="e.g., Retained in 1st grade"
              disabled={!canEdit}
            />
            <TextField
              label="Prior Intervention History"
              value={profile.educational_history?.prior_interventions}
              onChange={(v) => updateSection('educational_history', 'prior_interventions', v)}
              placeholder="Previous intervention support received"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Prior Special Education History (if any)"
              value={profile.educational_history?.prior_sped_history}
              onChange={(v) => updateSection('educational_history', 'prior_sped_history', v)}
              placeholder="Prior referral, evaluation, or services"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Relevant Academic History"
              value={profile.educational_history?.academic_history}
              onChange={(v) => updateSection('educational_history', 'academic_history', v)}
              placeholder="Notable academic background"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Notes About Curriculum/School Changes"
              value={profile.educational_history?.school_changes_notes}
              onChange={(v) => updateSection('educational_history', 'school_changes_notes', v)}
              placeholder="Relevant transitions or changes"
              multiline
              disabled={!canEdit}
            />
          </div>
        </ProfileSection>

        {/* 4. Attendance / Engagement Summary */}
        <ProfileSection 
          title="Attendance / Engagement Summary" 
          icon={Calendar} 
          status={status.attendance_engagement}
          testId="section-attendance"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField
              label="Attendance Concerns"
              value={profile.attendance_engagement?.attendance_concerns}
              onChange={(v) => updateSection('attendance_engagement', 'attendance_concerns', v)}
              options={YES_NO_UNKNOWN}
              disabled={!canEdit}
            />
            <TextField
              label="Approximate Absences (if known)"
              value={profile.attendance_engagement?.approximate_absences}
              onChange={(v) => updateSection('attendance_engagement', 'approximate_absences', v)}
              placeholder="e.g., 15 days this year"
              disabled={!canEdit}
            />
            <TextField
              label="Approximate Tardies (if known)"
              value={profile.attendance_engagement?.approximate_tardies}
              onChange={(v) => updateSection('attendance_engagement', 'approximate_tardies', v)}
              placeholder="e.g., 8 tardies"
              disabled={!canEdit}
            />
            <SelectField
              label="Engagement Concerns"
              value={profile.attendance_engagement?.engagement_concerns}
              onChange={(v) => updateSection('attendance_engagement', 'engagement_concerns', v)}
              options={YES_NO_UNKNOWN}
              disabled={!canEdit}
            />
            <div className="md:col-span-2">
              <TextField
                label="Notes About Attendance or Engagement"
                value={profile.attendance_engagement?.attendance_notes}
                onChange={(v) => updateSection('attendance_engagement', 'attendance_notes', v)}
                placeholder="Additional context about attendance patterns"
                multiline
                disabled={!canEdit}
              />
            </div>
            <SelectField
              label="Does Attendance Appear to Affect Intervention Progress?"
              value={profile.attendance_engagement?.affects_progress}
              onChange={(v) => updateSection('attendance_engagement', 'affects_progress', v)}
              options={YES_NO_UNKNOWN}
              disabled={!canEdit}
            />
            <TextField
              label="Explanation"
              value={profile.attendance_engagement?.affects_progress_explanation}
              onChange={(v) => updateSection('attendance_engagement', 'affects_progress_explanation', v)}
              placeholder="How attendance may be affecting progress"
              multiline
              disabled={!canEdit}
            />
          </div>
        </ProfileSection>

        {/* 5. Medical / Developmental Considerations */}
        <ProfileSection 
          title="Medical / Developmental Considerations" 
          icon={Heart} 
          status={status.medical_developmental}
          testId="section-medical"
        >
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
            <p className="text-xs text-blue-700">
              Information reported by parent/guardian or school records. This is informational only and does not constitute a diagnosis.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField
              label="Medical Concerns Reported"
              value={profile.medical_developmental?.medical_concerns}
              onChange={(v) => updateSection('medical_developmental', 'medical_concerns', v)}
              options={YES_NO_UNKNOWN}
              disabled={!canEdit}
            />
            <SelectField
              label="Developmental Concerns Reported"
              value={profile.medical_developmental?.developmental_concerns}
              onChange={(v) => updateSection('medical_developmental', 'developmental_concerns', v)}
              options={YES_NO_UNKNOWN}
              disabled={!canEdit}
            />
            <SelectField
              label="Vision/Hearing Concerns Reported"
              value={profile.medical_developmental?.vision_hearing_concerns}
              onChange={(v) => updateSection('medical_developmental', 'vision_hearing_concerns', v)}
              options={YES_NO_UNKNOWN}
              disabled={!canEdit}
            />
            <TextField
              label="Outside Diagnoses or Services (if voluntarily shared)"
              value={profile.medical_developmental?.outside_diagnoses}
              onChange={(v) => updateSection('medical_developmental', 'outside_diagnoses', v)}
              placeholder="e.g., Private speech therapy"
              disabled={!canEdit}
            />
            <TextField
              label="Medication/Health Factors (if voluntarily shared)"
              value={profile.medical_developmental?.medication_factors}
              onChange={(v) => updateSection('medical_developmental', 'medication_factors', v)}
              placeholder="Factors that may affect school performance"
              disabled={!canEdit}
            />
            <TextField
              label="Notes"
              value={profile.medical_developmental?.notes}
              onChange={(v) => updateSection('medical_developmental', 'notes', v)}
              placeholder="Additional relevant information"
              multiline
              disabled={!canEdit}
            />
          </div>
        </ProfileSection>

        {/* 6. Parent / Guardian Input */}
        <ProfileSection 
          title="Parent / Guardian Input" 
          icon={Users} 
          status={status.parent_guardian_input}
          testId="section-parent"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="Parent/Guardian Concerns"
              value={profile.parent_guardian_input?.parent_concerns}
              onChange={(v) => updateSection('parent_guardian_input', 'parent_concerns', v)}
              placeholder="Concerns shared by family"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Parent/Guardian Reported Strengths"
              value={profile.parent_guardian_input?.parent_strengths}
              onChange={(v) => updateSection('parent_guardian_input', 'parent_strengths', v)}
              placeholder="Strengths observed at home"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Home Observations"
              value={profile.parent_guardian_input?.home_observations}
              onChange={(v) => updateSection('parent_guardian_input', 'home_observations', v)}
              placeholder="Relevant observations from home environment"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Developmental/Medical/Educational History Shared"
              value={profile.parent_guardian_input?.developmental_history}
              onChange={(v) => updateSection('parent_guardian_input', 'developmental_history', v)}
              placeholder="History information provided by family"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Parent Priorities"
              value={profile.parent_guardian_input?.parent_priorities}
              onChange={(v) => updateSection('parent_guardian_input', 'parent_priorities', v)}
              placeholder="What matters most to the family"
              multiline
              disabled={!canEdit}
            />
            <DateField
              label="Contact Date"
              value={profile.parent_guardian_input?.contact_date}
              onChange={(v) => updateSection('parent_guardian_input', 'contact_date', v)}
              disabled={!canEdit}
            />
            <TextField
              label="Contact Method"
              value={profile.parent_guardian_input?.contact_method}
              onChange={(v) => updateSection('parent_guardian_input', 'contact_method', v)}
              placeholder="e.g., Phone call, conference, email"
              disabled={!canEdit}
            />
            <TextField
              label="Staff Member Who Collected Input"
              value={profile.parent_guardian_input?.staff_who_collected}
              onChange={(v) => updateSection('parent_guardian_input', 'staff_who_collected', v)}
              placeholder="Name of staff member"
              disabled={!canEdit}
            />
          </div>
        </ProfileSection>

        {/* 7. Teacher Input / Classroom Impact */}
        <ProfileSection 
          title="Teacher Input / Classroom Impact" 
          icon={GraduationCap} 
          status={status.teacher_classroom_input}
          testId="section-teacher"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="Teacher Concerns"
              value={profile.teacher_classroom_input?.teacher_concerns}
              onChange={(v) => updateSection('teacher_classroom_input', 'teacher_concerns', v)}
              placeholder="Primary concerns from teacher perspective"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Classroom Strengths"
              value={profile.teacher_classroom_input?.classroom_strengths}
              onChange={(v) => updateSection('teacher_classroom_input', 'classroom_strengths', v)}
              placeholder="Strengths observed in classroom"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Work Completion"
              value={profile.teacher_classroom_input?.work_completion}
              onChange={(v) => updateSection('teacher_classroom_input', 'work_completion', v)}
              placeholder="Observations about completing assignments"
              disabled={!canEdit}
            />
            <TextField
              label="Attention / Engagement"
              value={profile.teacher_classroom_input?.attention_engagement}
              onChange={(v) => updateSection('teacher_classroom_input', 'attention_engagement', v)}
              placeholder="Focus and engagement observations"
              disabled={!canEdit}
            />
            <TextField
              label="Peer Interaction"
              value={profile.teacher_classroom_input?.peer_interaction}
              onChange={(v) => updateSection('teacher_classroom_input', 'peer_interaction', v)}
              placeholder="Social interactions with peers"
              disabled={!canEdit}
            />
            <TextField
              label="Behavior / Executive Functioning Observations"
              value={profile.teacher_classroom_input?.behavior_observations}
              onChange={(v) => updateSection('teacher_classroom_input', 'behavior_observations', v)}
              placeholder="Behavioral and executive function observations"
              disabled={!canEdit}
            />
            <TextField
              label="Academic Impact"
              value={profile.teacher_classroom_input?.academic_impact}
              onChange={(v) => updateSection('teacher_classroom_input', 'academic_impact', v)}
              placeholder="How concerns affect academic performance"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Classroom Strategies Already Tried"
              value={profile.teacher_classroom_input?.strategies_tried}
              onChange={(v) => updateSection('teacher_classroom_input', 'strategies_tried', v)}
              placeholder="Supports and strategies attempted"
              multiline
              disabled={!canEdit}
            />
            <TextField
              label="Teacher-Reported Response to Supports"
              value={profile.teacher_classroom_input?.response_to_supports}
              onChange={(v) => updateSection('teacher_classroom_input', 'response_to_supports', v)}
              placeholder="How student responds to classroom supports"
              multiline
              disabled={!canEdit}
            />
            <DateField
              label="Date Input Collected"
              value={profile.teacher_classroom_input?.input_date}
              onChange={(v) => updateSection('teacher_classroom_input', 'input_date', v)}
              disabled={!canEdit}
            />
          </div>
        </ProfileSection>

        {/* 8. Intervention Access / Fidelity Notes */}
        <ProfileSection 
          title="Intervention Access / Fidelity Notes" 
          icon={ClipboardCheck} 
          status={status.intervention_fidelity}
          testId="section-fidelity"
        >
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
            <p className="text-xs text-blue-700">
              This section adds context to progress data. It does not replace progress monitoring.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField
              label="Intervention Delivered as Planned"
              value={profile.intervention_fidelity?.delivered_as_planned}
              onChange={(v) => updateSection('intervention_fidelity', 'delivered_as_planned', v)}
              options={DELIVERED_OPTIONS}
              disabled={!canEdit}
            />
            <TextField
              label="Barriers to Delivery"
              value={profile.intervention_fidelity?.barriers_to_delivery}
              onChange={(v) => updateSection('intervention_fidelity', 'barriers_to_delivery', v)}
              placeholder="What prevented consistent delivery?"
              disabled={!canEdit}
            />
            <TextField
              label="Missed Sessions or Inconsistent Access"
              value={profile.intervention_fidelity?.missed_sessions}
              onChange={(v) => updateSection('intervention_fidelity', 'missed_sessions', v)}
              placeholder="Describe gaps in intervention delivery"
              disabled={!canEdit}
            />
            <TextField
              label="Group Size Concerns"
              value={profile.intervention_fidelity?.group_size_concerns}
              onChange={(v) => updateSection('intervention_fidelity', 'group_size_concerns', v)}
              placeholder="Any issues with intervention group size"
              disabled={!canEdit}
            />
            <TextField
              label="Schedule Concerns"
              value={profile.intervention_fidelity?.schedule_concerns}
              onChange={(v) => updateSection('intervention_fidelity', 'schedule_concerns', v)}
              placeholder="Scheduling conflicts or timing issues"
              disabled={!canEdit}
            />
            <TextField
              label="Provider Notes"
              value={profile.intervention_fidelity?.provider_notes}
              onChange={(v) => updateSection('intervention_fidelity', 'provider_notes', v)}
              placeholder="Notes from intervention provider"
              multiline
              disabled={!canEdit}
            />
            <div className="md:col-span-2">
              <TextField
                label="Fidelity Notes"
                value={profile.intervention_fidelity?.fidelity_notes}
                onChange={(v) => updateSection('intervention_fidelity', 'fidelity_notes', v)}
                placeholder="Additional notes about intervention fidelity"
                multiline
                disabled={!canEdit}
              />
            </div>
          </div>
        </ProfileSection>

        {/* 9. Assessment / Screener Information */}
        <ProfileSection 
          title="Assessment / Screener Information" 
          icon={FileText} 
          status={status.assessment_screener}
          testId="section-assessment"
        >
          <div className="space-y-4">
            {(profile.assessment_screener_info?.entries || []).map((entry, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start mb-3">
                  <span className="text-sm font-medium text-gray-700">Assessment #{index + 1}</span>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeEntry('assessment_screener_info', index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <TextField
                    label="Screener/Assessment Name"
                    value={entry.screener_name}
                    onChange={(v) => updateEntry('assessment_screener_info', index, 'screener_name', v)}
                    placeholder="e.g., DIBELS, AIMSweb, STAR"
                    disabled={!canEdit}
                  />
                  <DateField
                    label="Date"
                    value={entry.date}
                    onChange={(v) => updateEntry('assessment_screener_info', index, 'date', v)}
                    disabled={!canEdit}
                  />
                  <SelectField
                    label="Area Assessed"
                    value={entry.area_assessed}
                    onChange={(v) => updateEntry('assessment_screener_info', index, 'area_assessed', v)}
                    options={SUPPORT_AREAS}
                    disabled={!canEdit}
                  />
                  <TextField
                    label="Score/Result"
                    value={entry.score_result}
                    onChange={(v) => updateEntry('assessment_screener_info', index, 'score_result', v)}
                    placeholder="e.g., 85 WPM, 42nd percentile"
                    disabled={!canEdit}
                  />
                  <TextField
                    label="Interpretation/Notes"
                    value={entry.interpretation}
                    onChange={(v) => updateEntry('assessment_screener_info', index, 'interpretation', v)}
                    placeholder="What does this result indicate?"
                    disabled={!canEdit}
                  />
                  <TextField
                    label="Source of Data"
                    value={entry.source}
                    onChange={(v) => updateEntry('assessment_screener_info', index, 'source', v)}
                    placeholder="e.g., School records, parent report"
                    disabled={!canEdit}
                  />
                </div>
              </div>
            ))}
            {canEdit && (
              <Button
                variant="outline"
                onClick={() => addEntry('assessment_screener_info', {
                  screener_name: '',
                  date: '',
                  area_assessed: '',
                  score_result: '',
                  interpretation: '',
                  source: ''
                })}
                className="w-full border-dashed"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Assessment/Screener
              </Button>
            )}
          </div>
        </ProfileSection>

        {/* 10. Review / Decision Notes */}
        <ProfileSection 
          title="Review / Decision Notes" 
          icon={MessageSquare} 
          status={status.review_decision}
          testId="section-review"
        >
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
            <p className="text-xs text-blue-700">
              Document team thinking at review points. This is not a full MTSS Review workflow—just a place to capture review-related context.
            </p>
          </div>
          <div className="space-y-4">
            {(profile.review_decision_notes?.entries || []).map((entry, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start mb-3">
                  <span className="text-sm font-medium text-gray-700">Review #{index + 1}</span>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeEntry('review_decision_notes', index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <DateField
                    label="Review Date"
                    value={entry.review_date}
                    onChange={(v) => updateEntry('review_decision_notes', index, 'review_date', v)}
                    disabled={!canEdit}
                  />
                  <SelectField
                    label="Team Decision"
                    value={entry.team_decision}
                    onChange={(v) => updateEntry('review_decision_notes', index, 'team_decision', v)}
                    options={DECISION_OPTIONS}
                    disabled={!canEdit}
                  />
                  <div className="md:col-span-2">
                    <TextField
                      label="Team Discussion Summary"
                      value={entry.discussion_summary}
                      onChange={(v) => updateEntry('review_decision_notes', index, 'discussion_summary', v)}
                      placeholder="Key points from team discussion"
                      multiline
                      disabled={!canEdit}
                    />
                  </div>
                  <TextField
                    label="Decision Considered"
                    value={entry.decision_considered}
                    onChange={(v) => updateEntry('review_decision_notes', index, 'decision_considered', v)}
                    placeholder="Options the team considered"
                    disabled={!canEdit}
                  />
                  <TextField
                    label="Rationale"
                    value={entry.rationale}
                    onChange={(v) => updateEntry('review_decision_notes', index, 'rationale', v)}
                    placeholder="Why this decision was made"
                    multiline
                    disabled={!canEdit}
                  />
                  <TextField
                    label="Follow-up Actions"
                    value={entry.follow_up_actions}
                    onChange={(v) => updateEntry('review_decision_notes', index, 'follow_up_actions', v)}
                    placeholder="Next steps to take"
                    multiline
                    disabled={!canEdit}
                  />
                  <DateField
                    label="Next Review Date"
                    value={entry.next_review_date}
                    onChange={(v) => updateEntry('review_decision_notes', index, 'next_review_date', v)}
                    disabled={!canEdit}
                  />
                </div>
              </div>
            ))}
            {canEdit && (
              <Button
                variant="outline"
                onClick={() => addEntry('review_decision_notes', {
                  review_date: new Date().toISOString().split('T')[0],
                  discussion_summary: '',
                  decision_considered: '',
                  team_decision: '',
                  rationale: '',
                  follow_up_actions: '',
                  next_review_date: ''
                })}
                className="w-full border-dashed"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Review Note
              </Button>
            )}
          </div>
        </ProfileSection>
      </div>

      {/* Bottom Save Button (for long pages) */}
      {canEdit && hasChanges && (
        <div className="sticky bottom-4 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white rounded-xl shadow-lg"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Profile
          </Button>
        </div>
      )}
    </div>
  );
}
